import axios from "axios";
import Url from "app/shared/constants/Url";
import { CHECKLIST_FORM_TYPE, WORK_PERMIT_API_ENDPOINT } from "../constants";
const qs = require("qs");

class ChecklistApiService {
  // Read fresh from localStorage on every call to avoid stale singleton values
  get userId() {
    return localStorage.getItem("userId");
  }

  get token() {
    return localStorage.getItem("token");
  }

  get springToken() {
    return localStorage.getItem("springToken");
  }

  // Helper to create FormData
  createFormData(data = {}) {
    const formData = new FormData();
    formData.append("auth_key", this.token);
    formData.append("user_id", this.userId);
    formData.append("form_type", CHECKLIST_FORM_TYPE);

    Object.entries(data).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item) => formData.append(key, item));
      } else if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });

    return formData;
  }

  // Fetch inspection form counts
  async fetchChecklistCounts(projectIds, startDate, endDate) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken,
      };

      if (projectIds && projectIds.length > 0) {
        params.project_id = projectIds.join(",");
      }
      if (startDate) {
        params.start_date = startDate;
      }
      if (endDate) {
        params.end_date = endDate;
      }

      const response = await axios.get(WORK_PERMIT_API_ENDPOINT.counts, {
        params,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching checklist counts:", error);
      throw error;
    }
  }

  // Fetch inspection recommendations
  async fetchRecommendations(projectIds, startDate, endDate, pageNo = 1) {
    const projectIdString = projectIds?.length > 0 ? projectIds.join(",") : "";

    const requestData = qs.stringify({
      auth_key: this.token,
      user_id: this.userId,
      form_type: CHECKLIST_FORM_TYPE,
      status: 0,
      page_no: pageNo,
      project_id: projectIdString,
      start_date: startDate,
      end_date: endDate,
    });

    try {
      const response = await axios.post(
        Url.formTypeGraph.auditRecommendation,
        requestData,
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching recommendations:", error);
      throw error;
    }
  }

  // Fetch checklist list for table view
  async fetchChecklistList(screen, projectIds, startDate, endDate) {
    const formData = this.createFormData({
      screen: screen,
      start_date: startDate,
      end_date: endDate,
    });

    if (projectIds && projectIds.length > 0) {
      projectIds.forEach((id) => formData.append("project_id", id));
    }

    try {
      const response = await axios.post(
        Url.formTypeGraph.inspectionList, // Adjust this URL based on your actual endpoint
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching checklist list:", error);
      throw error;
    }
  }

  // Fetch work permit schedule list
  async fetchScheduleList(filters = {}) {
    try {
      const {
        workPermitTemplateId,
        type,
        streamId,
        subStreamId,
        status,
        softDelete,
        page = 1,
        size = 10,
        sortBy = "creationTime",
        sortDirection = "desc",
        viewAll,
      } = filters;

      let params = {
        user_id: this.userId,
        auth_key: this.springToken,
        page,
        size,
        sortBy,
        sortDirection,
      };
      if (viewAll !== undefined) {
        params.viewAll = 1;
      }
      // Add optional filters only when provided
      if (workPermitTemplateId !== undefined && workPermitTemplateId !== null) {
        params.workPermitTemplateId = workPermitTemplateId;
      }
      if (type !== undefined && type !== null) {
        params.type = type;
      }
      if (streamId !== undefined && streamId !== null) {
        params.streamId = streamId;
      }
      if (subStreamId !== undefined && subStreamId !== null) {
        params.subStreamId = subStreamId;
      }
      if (status !== undefined && status !== null) {
        params.status = status;
      }
      if (softDelete !== undefined && softDelete !== null) {
        params.softDelete = softDelete;
      }

      console.log("aaaa ==", params);
      const response = await axios.get(WORK_PERMIT_API_ENDPOINT.scheduleList, {
        params,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching schedule list:", error);
      throw error;
    }
  }

  // Fetch work permit schedule details by ID
  async fetchScheduleDetails(scheduleId) {
    try {
      const response = await axios.get(WORK_PERMIT_API_ENDPOINT.details, {
        params: {
          user_id: this.userId,
          auth_key: this.springToken,
          sheduleId: scheduleId,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching schedule details:", error);
      throw error;
    }
  }

  // Delete work permit schedule by ID
  async deleteSchedule(scheduleId) {
    try {
      const response = await axios.delete(WORK_PERMIT_API_ENDPOINT.delete, {
        headers: {
          "Content-Type": "application/json",
        },
        params: {
          user_id: this.userId,
          auth_key: this.springToken,
          sheduleId: scheduleId,
        },
        data: {},
      });
      return response.data;
    } catch (error) {
      console.error("Error deleting schedule:", error);
      throw error;
    }
  }

  // Restore work permit schedule by ID
  async restoreSchedule(scheduleId) {
    try {
      const response = await axios.delete(WORK_PERMIT_API_ENDPOINT.restore, {
        headers: {
          "Content-Type": "application/json",
        },
        params: {
          user_id: this.userId,
          auth_key: this.springToken,
          sheduleId: scheduleId,
        },
        data: {},
      });
      return response.data;
    } catch (error) {
      console.error("Error restoring schedule:", error);
      throw error;
    }
  }

  // Close work permit
  async closeWorkPermit(scheduleId, closingRemark, closingReason, closingDate) {
    try {
      const response = await axios.post(
        WORK_PERMIT_API_ENDPOINT.close,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
          params: {
            user_id: this.userId,
            auth_key: this.springToken,
            schedule_id: scheduleId,
            closing_remark: closingRemark,
            closing_reason: closingReason,
            closing_date: closingDate,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error closing work permit:", error);
      throw error;
    }
  }

  // Extend work permit
  async extendWorkPermit(scheduleId, extendRemark, extendReason, endDate) {
    try {
      const response = await axios.post(
        WORK_PERMIT_API_ENDPOINT.extend,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
          params: {
            user_id: this.userId,
            auth_key: this.springToken,
            schedule_id: scheduleId,
            extend_remark: extendRemark,
            extend_reason: extendReason,
            end_date: endDate,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error extending work permit:", error);
      throw error;
    }
  }
  // Approve work permit schedule
  async approveWorkPermit(scheduleId, remarks = "success") {
    try {
      const response = await axios.post(
        WORK_PERMIT_API_ENDPOINT.approve,
        {
          schedule_id: Number(scheduleId),
          action: 1,
          remarks: remarks,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          params: {
            user_id: this.userId,
            auth_key: this.springToken,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error approving work permit:", error);
      throw error;
    }
  }

  // Decline/Reject work permit schedule
  async declineWorkPermit(
    scheduleId,
    declineReason = 1,
    declineRemark = "success",
  ) {
    try {
      const response = await axios.post(
        WORK_PERMIT_API_ENDPOINT.decline,
        {
          schedule_id: Number(scheduleId),
          decline_reason: declineReason,
          decline_remark: declineRemark,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          params: {
            user_id: this.userId,
            auth_key: this.springToken,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error declining work permit:", error);
      throw error;
    }
  }
}

export default new ChecklistApiService();
